import React, { useEffect, useState } from "react";
import SafeIcon from "../../../common/SafeIcon";
import { FiAlertTriangle, FiFileText, FiX } from "react-icons/fi";
import { clientDocumentService } from "../../../services/client/clientDocumentService";

const categories = [
  ["general", "General"], ["tax", "Tax"], ["bank_statement", "Bank statement"],
  ["financial_statement", "Financial statement"], ["contract", "Contract"],
  ["invoice", "Invoice"], ["receipt", "Receipt"], ["payroll", "Payroll"],
  ["legal", "Legal"], ["other", "Other"],
];

function sizeLabel(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value)) return "Unavailable";
  if (value < 1024) return `${value} bytes`;
  return value < 1048576 ? `${(value / 1024).toFixed(1)} KB` : `${(value / 1048576).toFixed(1)} MB`;
}

function dateLabel(value) {
  if (!value) return "Unavailable";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unavailable" : date.toLocaleString();
}

export default function ClientDocumentDetailsModal({
  organizationId,
  document: selectedDocument,
  currentUserId,
  canManage,
  onClose,
  onSaved,
}) {
  const [document, setDocument] = useState(null);
  const [category, setCategory] = useState("general");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState("client_only");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const requestKey = selectedDocument ? `${organizationId}:${selectedDocument.id}` : "";

  useEffect(() => {
    if (!selectedDocument || !organizationId) {
      setDocument(null);
      return undefined;
    }
    let current = true;
    setDocument(null);
    setError("");
    clientDocumentService.getClientDocumentDetails({
      organizationId,
      documentId: selectedDocument.id,
    }).then((result) => {
      if (!current) return;
      setDocument(result);
      setCategory(result.category);
      setDescription(result.description || "");
      setVisibility(result.visibility);
    }).catch((loadError) => {
      if (current) setError(loadError?.message || "Unable to load document details.");
    });
    return () => { current = false; };
  }, [requestKey]);

  useEffect(() => {
    if (!selectedDocument) return undefined;
    const keydown = (event) => event.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", keydown);
    return () => window.removeEventListener("keydown", keydown);
  }, [selectedDocument, busy, onClose]);

  if (!selectedDocument) return null;

  const save = async (event) => {
    event.preventDefault();
    if (!canManage || busy) return;
    setBusy(true);
    setError("");
    try {
      const updated = await clientDocumentService.updateClientDocument({
        organizationId,
        documentId: selectedDocument.id,
        updates: { category, description, visibility },
      });
      setDocument(updated);
      setEditing(false);
      await onSaved?.();
    } catch (saveError) {
      setError(saveError?.message || "Document details could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  const value = document || selectedDocument;
  const readOnly = !editing || !canManage;

  return (
    <div className="fixed inset-0 z-[115] flex items-end justify-center bg-slate-950/50 p-0 backdrop-blur-sm sm:items-center sm:p-4" onMouseDown={(event) => event.target === event.currentTarget && !busy && onClose()}>
      <section role="dialog" aria-modal="true" aria-labelledby="document-details-title" className="max-h-[94dvh] w-full overflow-y-auto rounded-t-3xl bg-white shadow-2xl sm:max-w-2xl sm:rounded-3xl">
        <header className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 sm:px-7">
          <div><p className="text-xs font-bold uppercase tracking-widest text-violet-700">Document information</p><h2 id="document-details-title" className="mt-1 break-all text-lg font-black text-slate-900">{value.file_name}</h2></div>
          <button type="button" aria-label="Close details" onClick={onClose} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100"><SafeIcon icon={FiX} /></button>
        </header>
        <form onSubmit={save} className="space-y-5 p-5 sm:p-7">
          {error && <p role="alert" className="flex gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"><SafeIcon icon={FiAlertTriangle} className="mt-0.5 shrink-0" />{error}</p>}
          {!document && !error && <p className="text-sm text-slate-500">Loading document details…</p>}
          <div className="grid gap-3 sm:grid-cols-2">
            <Info label="File type" value={value.file_type} />
            <Info label="File size" value={sizeLabel(value.file_size)} />
            <Info label="Uploaded by" value={value.uploaded_by === currentUserId ? "You" : "Business team"} />
            <Info label="Upload date" value={dateLabel(value.created_at)} />
            <Info label="Status" value={value.status} />
          </div>
          <div className="space-y-4 rounded-2xl border border-slate-200 p-4">
            <label className="block text-xs font-bold text-slate-600">Category
              <select disabled={readOnly || busy} value={category} onChange={(event) => setCategory(event.target.value)} className="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal text-slate-800 disabled:bg-slate-50">
                {categories.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </select>
            </label>
            <label className="block text-xs font-bold text-slate-600">Description
              <textarea disabled={readOnly || busy} value={description} onChange={(event) => setDescription(event.target.value)} rows="3" maxLength="1000" className="mt-2 w-full rounded-xl border border-slate-200 p-3 text-sm font-normal text-slate-800 disabled:bg-slate-50" />
            </label>
            <label className="block text-xs font-bold text-slate-600">Visibility
              <select disabled={readOnly || busy} value={visibility} onChange={(event) => setVisibility(event.target.value)} className="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal text-slate-800 disabled:bg-slate-50">
                <option value="client_only">Private to this business</option><option value="shared_with_accountant">Shared with accountant</option>
              </select>
            </label>
          </div>
          <footer className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
            {canManage && !editing && <button type="button" onClick={() => setEditing(true)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700">Edit details</button>}
            {editing && <button type="button" disabled={busy} onClick={() => { setEditing(false); setCategory(value.category); setDescription(value.description || ""); setVisibility(value.visibility); }} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600">Cancel edit</button>}
            {editing && <button type="submit" disabled={busy || !document} className="rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-violet-700 disabled:opacity-50">{busy ? "Saving…" : "Save changes"}</button>}
            {!editing && <button type="button" onClick={onClose} className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white">Close</button>}
          </footer>
          {document && <p className="flex items-start gap-2 text-[11px] text-slate-400"><SafeIcon icon={FiFileText} className="mt-0.5" />Organization, uploader, file path, original name, type, and size are fixed metadata.</p>}
        </form>
      </section>
    </div>
  );
}

function Info({ label, value }) {
  return <div className="min-w-0 rounded-xl bg-slate-50 p-3"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p><p className="mt-1 break-words text-sm font-semibold text-slate-800">{value || "Unavailable"}</p></div>;
}